/**
 * Explicit desk / Megan ticket writes are sticky.
 *
 * Emerson (or Megan after a spoken yes) Marks Complete, cancels, or reopens —
 * that status stays until a human undoes it. SimConnect, Nanode Pull, parking,
 * and advanceLiveTicket may not silently demote it.
 */
import { normalizeFlightHint } from "./flightNumber";
import { isOpenOpsTicket } from "./opsBoard";
import { isStatusRegression, mergeRevisionRow } from "./rowRevision";
import { mergeTicketAtd } from "./ticketAtd";
import type { FlightLogEntry, Mission, MissionStatus } from "./types";

export type TicketStatusAuthority = "desk" | "megan" | "sim";

export function isHumanTicketAuthority(value?: string | null): boolean {
  return value === "desk" || value === "megan";
}

export function hydrateTicketStatusAuthority(raw?: string | null): TicketStatusAuthority | undefined {
  if (raw === "desk" || raw === "megan" || raw === "sim") return raw;
  return undefined;
}

export function sameOpsTicket(
  a: Pick<Mission, "id" | "callsign" | "flightNumber">,
  b: Pick<Mission, "id" | "callsign" | "flightNumber">,
): boolean {
  if (a.id && b.id && a.id === b.id) return true;
  const left = normalizeFlightHint(a.flightNumber || a.callsign);
  const right = normalizeFlightHint(b.flightNumber || b.callsign);
  return Boolean(left && right && left === right);
}

export function isStickyCompleted(
  mission: Pick<Mission, "status" | "lifecycle"> | null | undefined,
): boolean {
  if (!mission) return false;
  return mission.status === "completed" || mission.lifecycle === "completed";
}

/** Open / earlier status that must not overwrite a local Completed / Cancelled. */
export function isOlderOpenTicketStatus(status: MissionStatus | undefined): boolean {
  return status === "pending" || status === "in-progress" || status == null;
}

/**
 * Local completed (or a human cancel) wins over an older Nanode snapshot that
 * still has the CEL#### as pending / airborne.
 */
export function shouldKeepLocalTicketStatus(local: Mission, remote: Mission): boolean {
  if (isStickyCompleted(local) && isOlderOpenTicketStatus(remote.status) && remote.lifecycle !== "completed") {
    return true;
  }
  if (
    local.status === "cancelled" &&
    isHumanTicketAuthority(local.statusAuthority) &&
    isOpenOpsTicket(remote.status)
  ) {
    return true;
  }
  return isStatusRegression(local, remote);
}

export function applyLocalTicketStatus(remote: Mission, local: Mission): Mission {
  return {
    ...remote,
    status: local.status,
    lifecycle: local.lifecycle ?? (local.status === "completed" ? "completed" : local.lifecycle),
    completedAt: local.completedAt ?? remote.completedAt,
    acceptedAt: local.acceptedAt ?? remote.acceptedAt,
    startedAt: local.startedAt ?? remote.startedAt,
    arrivedAtUtc: local.arrivedAtUtc ?? remote.arrivedAtUtc,
    ...mergeTicketAtd(local, remote),
    hoursApplied: local.hoursApplied ?? remote.hoursApplied,
    ledgerPosted: local.ledgerPosted ?? remote.ledgerPosted,
    statusAuthority: local.statusAuthority ?? remote.statusAuthority,
    statusAuthorityAt: local.statusAuthorityAt ?? remote.statusAuthorityAt,
    activeBoundAt: local.activeBoundAt ?? remote.activeBoundAt,
    cancelledAt: local.cancelledAt ?? remote.cancelledAt,
    cancelReason: local.cancelReason ?? remote.cancelReason,
    cancelReasonNote: local.cancelReasonNote ?? remote.cancelReasonNote,
    preCancelStatus: local.preCancelStatus ?? remote.preCancelStatus,
  };
}

export function preserveAuthoritativeTickets(local: Mission[], remote: Mission[]): Mission[] {
  return remote.map((incoming) => {
    const held = local.find((row) => sameOpsTicket(row, incoming));
    if (!held) return incoming;
    return mergeRevisionRow(
      held as unknown as Record<string, unknown>,
      incoming as unknown as Record<string, unknown>,
    ).row as Mission;
  });
}

export function preserveAuthoritativeLogs(
  local: FlightLogEntry[],
  remote: FlightLogEntry[],
): FlightLogEntry[] {
  return remote.map((incoming) => {
    const held = local.find((row) => row.id === incoming.id || (row.tripId && row.tripId === incoming.tripId));
    if (!held) return incoming;
    if (held.status === "complete" && incoming.status !== "complete" && incoming.status !== "cancelled") {
      return {
        ...incoming,
        status: "complete",
        departedAtUtc: held.departedAtUtc ?? incoming.departedAtUtc,
        departedAtLocal: held.departedAtLocal ?? incoming.departedAtLocal,
        actualDepartureUtc: held.actualDepartureUtc ?? incoming.actualDepartureUtc,
        arrivedAtUtc: held.arrivedAtUtc ?? incoming.arrivedAtUtc,
        arrivedAtLocal: held.arrivedAtLocal ?? incoming.arrivedAtLocal,
      };
    }
    if (held.status === "cancelled" && incoming.status !== "cancelled" && incoming.status !== "complete") {
      return {
        ...incoming,
        status: "cancelled",
        cancelReason: held.cancelReason ?? incoming.cancelReason,
        cancelReasonNote: held.cancelReasonNote ?? incoming.cancelReasonNote,
      };
    }
    return incoming;
  });
}

/** Live SimConnect / SI must not write a lesser lifecycle over Completed. */
export function liveMayWriteTicket(
  mission: Pick<Mission, "status" | "lifecycle" | "statusAuthority"> | null | undefined,
): boolean {
  if (!mission) return false;
  if (isStickyCompleted(mission)) return false;
  if (mission.status === "cancelled") return false;
  return true;
}

export function stampTicketAuthority(
  mission: Mission,
  authority: TicketStatusAuthority,
  at: string,
): Mission {
  return {
    ...mission,
    statusAuthority: authority,
    statusAuthorityAt: at,
  };
}

/** Live SI session is this CEL / pair — detach it after a human complete. */
export function siSessionMatchesTicket(
  session: { callsign?: string; callsignIcao?: string; originIcao?: string; destIcao?: string } | null | undefined,
  mission: Pick<Mission, "callsign" | "flightNumber" | "originIcao" | "destIcao">,
): boolean {
  if (!session) return false;
  const ticket = normalizeFlightHint(mission.flightNumber || mission.callsign);
  const call = normalizeFlightHint(session.callsign || session.callsignIcao);
  if (ticket && call && (call === ticket || call.includes(ticket) || ticket.includes(call))) return true;
  const origin = normalizeFlightHint(session.originIcao);
  const dest = normalizeFlightHint(session.destIcao);
  return Boolean(
    origin &&
      dest &&
      origin === normalizeFlightHint(mission.originIcao) &&
      dest === normalizeFlightHint(mission.destIcao),
  );
}
